'use client';

import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  alpha,
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ResponsiveStyleValue } from '@mui/system';

import { useT } from '@/hooks';

export interface FilterOption {
  label: string;
  value: string;
}

interface FilterMenuItemProps {
  option: FilterOption;
  isSelected: boolean;
  onSelect: (value: string) => void;
}

function FilterMenuItem({ option, isSelected, onSelect }: FilterMenuItemProps) {
  const theme = useTheme();
  const { t } = useT();

  const handleItemClick = () => {
    onSelect(option.value);
  };

  return (
    <MenuItem
      onClick={handleItemClick}
      selected={isSelected}
      sx={{
        fontSize: '0.9rem',
        fontWeight: isSelected ? 600 : 400,
        py: 1,
        '&.Mui-selected': {
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          },
        },
      }}
    >
      {t(`filter.${option.label}`, { defaultValue: option.label })}
    </MenuItem>
  );
}

interface FilterDropdownProps {
  label?: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  minWidth?: ResponsiveStyleValue<number | string>;
  height?: number | string;
  align?: 'left' | 'right';
  /** Stretches the trigger button to fill its container (e.g. a mobile dropdown). */
  fullWidth?: boolean;
  /**
   * 'button' (default) — bordered pill trigger, unchanged.
   * 'heading' — the selected option reads as a page heading (large, bold, no
   * border/background) with a small arrow to its right, sized to content.
   * 'plain' — no border/background, just the normal-sized label + arrow.
   */
  variant?: 'button' | 'heading' | 'plain';
  /** Font size of the selected-option label ('button' variant only). Default '0.8125rem'. */
  fontSize?: ResponsiveStyleValue<string>;
}

export function FilterDropdown({
  label,
  value,
  options,
  onChange,
  minWidth = 140,
  height = 34,
  align: alignProp,
  fullWidth = false,
  variant = 'button',
  fontSize = '0.8125rem',
}: FilterDropdownProps) {
  const theme = useTheme();
  const { t } = useT();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const align = alignProp !== undefined ? alignProp : isMobile ? 'left' : 'right';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    handleClose();
  };

  const selectedOption = options.find((o) => o.value === value) || options[0];

  const renderMenuItems = (option: FilterOption) => (
    <FilterMenuItem
      key={option.value}
      option={option}
      isSelected={option.value === value}
      onSelect={handleSelect}
    />
  );

  const menuItems = options.map(renderMenuItems);

  const isHeading = variant === 'heading';
  const isPlain = variant === 'plain';
  const isTransparent = isHeading || isPlain;

  return (
    <Box sx={{ position: 'relative', width: fullWidth && !isTransparent ? '100%' : 'fit-content' }}>
      <Button
        id={`filter-button-${label || 'dropdown'}`}
        aria-controls={open ? `filter-menu-${label || 'dropdown'}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant={isTransparent ? 'text' : 'outlined'}
        disableElevation
        fullWidth={fullWidth && !isTransparent}
        onClick={handleClick}
        endIcon={
          <KeyboardArrowDownIcon
            sx={isHeading ? { fontSize: '1.5rem !important' } : { fontSize: '1.1rem !important' }}
          />
        }
        sx={
          isTransparent
            ? {
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                color: theme.palette.text.primary,
                bgcolor: 'transparent',
                '&:hover': { bgcolor: 'transparent' },
              }
            : {
                textTransform: 'none',
                minWidth: minWidth,
                height: height,
                borderRadius: '17px',
                padding: '4px 14px',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.12)',
                color: theme.palette.text.primary,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
                justifyContent: 'space-between',
                '&:hover': {
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.04)',
                  borderColor: theme.palette.text.secondary,
                },
              }
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
          {label && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                fontWeight: 400,
              }}
            >
              {t(`filter.${label}`, { defaultValue: label })}
            </Typography>
          )}
          <Typography
            sx={
              isHeading
                ? {
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }
                : {
                    fontSize,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }
            }
          >
            {t(`filter.${selectedOption?.label}`, { defaultValue: selectedOption?.label })}
          </Typography>
        </Box>
      </Button>

      <Menu
        id={`filter-menu-${label || 'dropdown'}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `filter-button-${label || 'dropdown'}`,
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: Math.max(typeof minWidth === 'number' ? minWidth : 140, 140),
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0px 10px 30px rgba(0,0,0,0.5)',
            mt: 1,
            // A flat height (rather than the old xs:200px/sm:260px/md:60vh
            // spread) so the same number of options is visible without
            // scrolling regardless of breakpoint — the huge md:60vh could fit
            // every option with no scroll at all while xs:200px cut most of
            // them off, making the list look different per screen size even
            // though `options` itself never changes. overflowY is explicit
            // (MUI's Popover Paper defaults to auto, but this guarantees it).
            maxHeight: 320,
            overflowY: 'auto',
          },
        }}
        transformOrigin={{ horizontal: align, vertical: 'top' }}
        anchorOrigin={{ horizontal: align, vertical: 'bottom' }}
      >
        {menuItems}
      </Menu>
    </Box>
  );
}
